import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { CheckCircle2, Loader2, AlertTriangle, Clock, Train, MessageCircle } from "lucide-react";
import Footer from "../components/Footer";
import { whatsappUrl } from "../lib/whatsapp";

// Poll Stripe checkout status (per Emergent Stripe playbook):
// up to 5 attempts, 2s apart, before showing a "still pending" message.
const MAX_ATTEMPTS = 5;
const POLL_INTERVAL_MS = 2000;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const orderRef = params.get("order");

  const [state, setState] = useState({
    status: "checking", // checking | paid | pending | expired | timeout | error
    payment_status: null,
    amount_total: null,
    currency: null,
    error: null,
  });
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setState((s) => ({ ...s, status: "error", error: "Missing session reference." }));
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({
            status: "paid",
            payment_status: data.payment_status,
            amount_total: data.amount_total,
            currency: data.currency,
            error: null,
          });
          return;
        }
        if (data.status === "expired") {
          setState((s) => ({ ...s, status: "expired", payment_status: data.payment_status }));
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          // In preview mode (sk_test_emergent) live retrieval is not available;
          // surface a friendly "pending — confirmation by email" message.
          setState((s) => ({ ...s, status: "pending", payment_status: data.payment_status, amount_total: data.amount_total, currency: data.currency }));
          return;
        }
        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        const code = e.response?.status;
        if (code === 404) {
          setState((s) => ({ ...s, status: "error", error: "Payment session not found. If you completed checkout, please check your email." }));
          return;
        }
        setState((s) => ({ ...s, status: "error", error: formatApiError(e.response?.data?.detail) || e.message }));
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sessionId]);

  const fmtMoney = () => {
    if (state.amount_total == null) return "";
    const v = state.amount_total / 100;
    const cur = (state.currency || "eur").toUpperCase();
    return `${cur === "EUR" ? "€" : cur + " "}${v.toLocaleString()}`;
  };

  return (
    <div data-testid="checkout-success-page" className="pt-32 pb-16 min-h-screen">
      <div className="max-w-2xl mx-auto px-6">
        <div className="iq-card p-10 md:p-12 text-center animate-fade-up">
          {state.status === "checking" && (
            <>
              <Loader2 data-testid="cs-loading" className="h-12 w-12 text-gold mx-auto mb-5 animate-spin" />
              <h1 className="font-display text-cream text-3xl md:text-4xl tracking-wider mb-2">Confirming your payment…</h1>
              <p className="text-cream-dim text-sm">Stripe is finalising the transaction. This usually takes a couple of seconds.</p>
            </>
          )}

          {state.status === "paid" && (
            <>
              <CheckCircle2 data-testid="cs-paid" className="h-14 w-14 text-gold mx-auto mb-5" />
              <h1 className="font-display text-cream text-4xl md:text-5xl tracking-wider mb-2">Payment received</h1>
              {orderRef && (
                <p className="text-cream-dim text-sm font-display tracking-[0.3em] mb-4">REF #{orderRef.slice(0, 8).toUpperCase()}</p>
              )}
              {state.amount_total != null && (
                <p className="font-display text-gold text-2xl mb-4" data-testid="cs-amount">{fmtMoney()} paid</p>
              )}
              <p className="font-serif italic text-cream text-lg leading-relaxed mb-6">
                "Your inventory is secured. The conductor is preparing your binding itinerary — expect it in your inbox within one business day."
              </p>
              <div className="text-sm text-cream-dim mb-6 inline-flex items-center gap-2">
                <Train className="h-4 w-4 text-gold" />
                A confirmation email is on its way.
              </div>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <Link to="/dashboard" className="iq-btn-primary">Open dashboard</Link>
                <a
                  href={whatsappUrl({ orderRef: orderRef ? orderRef.slice(0, 8).toUpperCase() : undefined })}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="cs-whatsapp"
                  className="iq-btn-secondary"
                >
                  <MessageCircle className="h-4 w-4" /> WhatsApp the conductor
                </a>
                <Link to="/routes" className="iq-btn-ghost">Browse more tours</Link>
              </div>
            </>
          )}

          {state.status === "expired" && (
            <>
              <AlertTriangle data-testid="cs-expired" className="h-12 w-12 text-accent-red mx-auto mb-5" />
              <h1 className="font-display text-cream text-3xl tracking-wider mb-2">Payment session expired</h1>
              <p className="text-cream-dim text-sm mb-6">No charge was made. Your reservation is still on file — retry payment from your dashboard or cart.</p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <Link to="/cart" className="iq-btn-primary">Back to cart</Link>
                <Link to="/dashboard" className="iq-btn-secondary">Dashboard</Link>
              </div>
            </>
          )}

          {state.status === "timeout" && (
            <>
              <Clock data-testid="cs-timeout" className="h-12 w-12 text-gold mx-auto mb-5" />
              <h1 className="font-display text-cream text-3xl tracking-wider mb-2">Still processing…</h1>
              <p className="text-cream-dim text-sm mb-6">Stripe is taking longer than usual. Your receipt will arrive by email; you can also refresh this page in a minute.</p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <button data-testid="cs-refresh" onClick={() => window.location.reload()} className="iq-btn-primary">Refresh status</button>
                <Link to="/dashboard" className="iq-btn-secondary">Dashboard</Link>
              </div>
            </>
          )}

          {state.status === "pending" && (
            <>
              <Clock data-testid="cs-pending" className="h-12 w-12 text-gold mx-auto mb-5" />
              <h1 className="font-display text-cream text-3xl md:text-4xl tracking-wider mb-2">Thank you — confirmation pending</h1>
              {orderRef && (
                <p className="text-cream-dim text-sm font-display tracking-[0.3em] mb-4">REF #{orderRef.slice(0, 8).toUpperCase()}</p>
              )}
              <p className="font-serif italic text-cream text-base leading-relaxed mb-6">
                "Stripe is still confirming the transaction. Your receipt will arrive by email in a moment — refresh this page to see the latest status, or head to your dashboard."
              </p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <button data-testid="cs-refresh-pending" onClick={() => window.location.reload()} className="iq-btn-primary">Refresh status</button>
                <Link to="/dashboard" className="iq-btn-secondary">Dashboard</Link>
              </div>
            </>
          )}

          {state.status === "error" && (
            <>
              <AlertTriangle data-testid="cs-error" className="h-12 w-12 text-accent-red mx-auto mb-5" />
              <h1 className="font-display text-cream text-3xl tracking-wider mb-2">Couldn't confirm your payment</h1>
              <p className="text-cream-dim text-sm mb-2">{state.error || "Stripe didn't respond. If you were charged, it's safe — our team will reconcile within an hour."}</p>
              <p className="text-cream-dim text-xs mb-6">Reference: {sessionId?.slice(-12) || "—"}</p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <Link to="/dashboard" className="iq-btn-primary">Open dashboard</Link>
                <Link to="/" className="iq-btn-secondary">Return home</Link>
              </div>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
